import { useState } from 'react';
import { ArrowRight, AlertCircle } from 'lucide-react';
import { theme } from '../../styles/theme';
import { useIsMobile } from '../../hooks/useIsMobile';
import { ConnieAvatar } from '../../components/ConnieAvatar';

interface EmailPanelProps {
  onSubmit: (email: string) => void;
  error?: string | null;
}

const REQUIRE_BUSINESS_EMAIL = process.env.REACT_APP_REQUIRE_BUSINESS_EMAIL !== 'false';

const PERSONAL_EMAIL_DOMAINS = [
  'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'aol.com',
  'icloud.com', 'mail.com', 'protonmail.com', 'zoho.com', 'yandex.com',
  'gmx.com', 'live.com', 'msn.com', 'me.com', 'mac.com',
];

function isPersonalEmail(email: string): boolean {
  const parts = email.split('@');
  if (parts.length !== 2 || !parts[1]) return false;
  const domain = parts[1].toLowerCase();
  return PERSONAL_EMAIL_DOMAINS.includes(domain);
}

export function EmailPanel({ onSubmit, error }: EmailPanelProps) {
  const mobile = useIsMobile();
  const [email, setEmail] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSubmit = () => {
    setValidationError(null);

    if (!email.trim()) {
      setValidationError('Email address is required');
      return;
    }

    if (!valid) {
      setValidationError('Please enter a valid email address');
      return;
    }

    if (REQUIRE_BUSINESS_EMAIL && isPersonalEmail(email)) {
      setValidationError('Please use a business email address');
      return;
    }

    onSubmit(email);
  };

  return (
    <div style={{ maxWidth: '36rem', margin: '0 auto', padding: mobile ? '1.5rem 1rem' : '3rem 1rem', display: 'flex', flexDirection: 'column', gap: mobile ? '1.5rem' : '3rem' }}>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: theme.spacing.xl, textAlign: 'center' }}>
        <ConnieAvatar size={mobile ? 64 : 96} />
        <div>
          <h2 style={{ fontFamily: theme.fontFamily.display, fontSize: mobile ? theme.fontSize['2xl'] : theme.fontSize['3xl'], fontWeight: theme.fontWeight.light, letterSpacing: '-0.02em', color: theme.colors.navy }}>
            "Stay in <span style={{ fontFamily: theme.fontFamily.serif, fontStyle: 'italic', fontWeight: 'bold', color: theme.colors.teal }}>touch?</span>"
          </h2>
          <p style={{ color: theme.colors.textMuted, fontWeight: theme.fontWeight.light, marginTop: theme.spacing.sm }}>
            Share your email and I'll send you a copy of your itinerary.
          </p>
        </div>
      </div>

      <div style={{ maxWidth: '30rem', margin: '0 auto', width: '100%' }}>
        <label style={{ fontSize: theme.fontSize.xs, fontWeight: theme.fontWeight.bold, textTransform: 'uppercase', letterSpacing: '0.15em', color: theme.colors.muted, display: 'block', marginBottom: theme.spacing.sm }}>
          Email Address
        </label>
        <input
          type="email"
          placeholder="you@company.com"
          className="ghost-input"
          style={{ textAlign: 'left', fontSize: '1.1rem' }}
          value={email}
          onChange={e => { setEmail(e.target.value); setValidationError(null); }}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(); }}
          autoFocus
        />
      </div>

      {(validationError || error) && (
        <div style={{
          background: 'rgba(239,68,68,0.1)', border: `1px solid ${theme.colors.error}`, borderRadius: '1.5rem',
          padding: `${theme.spacing.lg} ${theme.spacing.xl}`, textAlign: 'center',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: theme.spacing.sm,
        }}>
          <AlertCircle size={24} color={theme.colors.error} />
          <p style={{ color: theme.colors.errorText, fontSize: theme.fontSize.sm, fontWeight: theme.fontWeight.light }}>
            {validationError || error}
          </p>
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'center', paddingTop: theme.spacing.xl }}>
        <button
          className="btn-primary"
          onClick={handleSubmit}
          disabled={!valid}
          style={{
            padding: `${theme.spacing.lg} ${theme.spacing['2xl']}`,
            opacity: valid ? 1 : 0.2,
            fontSize: '10px',
            textTransform: 'uppercase',
            letterSpacing: '0.15em',
            cursor: valid ? 'pointer' : 'default',
          }}
        >
          Send My Itinerary <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
}
